import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, type WheelEvent } from 'react'

type DesktopImageTransform = { scale: number; x: number; y: number }
type DragPoint = { x: number; y: number }
type ViewportSize = { width: number; height: number }

const DESKTOP_MIN_SCALE = 1
const DESKTOP_MAX_SCALE = 4
const DESKTOP_WHEEL_ZOOM_STEP = 0.0015
const DESKTOP_WHEEL_ZOOM_TRANSITION = 'transform 0.12s ease-out'
const DESKTOP_WHEEL_ZOOM_TRANSITION_TIMEOUT = 140

const getLimitedScale = (scale: number) => Math.min(Math.max(scale, DESKTOP_MIN_SCALE), DESKTOP_MAX_SCALE)

const getLimitedTranslate = (value: number, scale: number, viewportSize: number) => {
	const maxTranslate = (viewportSize * (scale - 1)) / 2
	return Math.min(Math.max(value, -maxTranslate), maxTranslate)
}

const getLimitedTransform = (transform: DesktopImageTransform, viewportSize: ViewportSize): DesktopImageTransform => {
	return {
		scale: transform.scale,
		x: getLimitedTranslate(transform.x, transform.scale, viewportSize.width),
		y: getLimitedTranslate(transform.y, transform.scale, viewportSize.height)
	}
}

const getTransformFromWheel = ({
	currentTransform,
	nextScale,
	originX,
	originY,
	imageElement,
	viewportElement
}: {
	currentTransform: DesktopImageTransform
	nextScale: number
	originX: number
	originY: number
	imageElement: HTMLImageElement | null
	viewportElement: HTMLDivElement
}) => {
	const imageRect = imageElement?.getBoundingClientRect()
	const viewportRect = viewportElement.getBoundingClientRect()
	const baseCenterX = imageRect
		? imageRect.left + imageRect.width / 2 - currentTransform.x
		: viewportRect.left + viewportRect.width / 2
	const baseCenterY = imageRect
		? imageRect.top + imageRect.height / 2 - currentTransform.y
		: viewportRect.top + viewportRect.height / 2
	const scaleRatio = nextScale / currentTransform.scale
	const translateX = originX - baseCenterX - (originX - baseCenterX - currentTransform.x) * scaleRatio
	const translateY = originY - baseCenterY - (originY - baseCenterY - currentTransform.y) * scaleRatio

	return getLimitedTransform({ scale: nextScale, x: translateX, y: translateY }, viewportRect)
}

export const useDesktopGalleryWheelZoom = ({ enabled }: { enabled: boolean }) => {
	const imageElementRef = useRef<HTMLImageElement | null>(null)
	const transformRef = useRef<DesktopImageTransform>({ scale: 1, x: 0, y: 0 })
	const transformFrameRef = useRef<number | null>(null)
	const dragPointRef = useRef<DragPoint | null>(null)
	const dragPointerIdRef = useRef<number | null>(null)
	const dragViewportSizeRef = useRef<ViewportSize | null>(null)
	const transitionTimeoutRef = useRef<number | null>(null)

	const clearWheelTransition = useCallback(() => {
		if (transitionTimeoutRef.current !== null) {
			window.clearTimeout(transitionTimeoutRef.current)
			transitionTimeoutRef.current = null
		}

		const imageElement = imageElementRef.current
		if (imageElement) imageElement.style.transition = ''
	}, [])

	const scheduleWheelTransitionReset = useCallback(() => {
		if (transitionTimeoutRef.current !== null) window.clearTimeout(transitionTimeoutRef.current)

		transitionTimeoutRef.current = window.setTimeout(() => {
			transitionTimeoutRef.current = null
			const imageElement = imageElementRef.current
			if (imageElement) imageElement.style.transition = ''
		}, DESKTOP_WHEEL_ZOOM_TRANSITION_TIMEOUT)
	}, [])

	const applyTransform = useCallback(() => {
		const imageElement = imageElementRef.current
		if (!imageElement) return

		const { scale, x, y } = transformRef.current
		imageElement.style.transform = scale > 1 ? `translate3d(${x}px, ${y}px, 0) scale(${scale})` : ''
	}, [])

	const scheduleTransform = useCallback(() => {
		if (transformFrameRef.current !== null) return

		transformFrameRef.current = requestAnimationFrame(() => {
			transformFrameRef.current = null
			applyTransform()
		})
	}, [applyTransform])

	const resetWheelZoom = useCallback(() => {
		transformRef.current = { scale: 1, x: 0, y: 0 }
		dragPointRef.current = null
		dragPointerIdRef.current = null
		dragViewportSizeRef.current = null
		clearWheelTransition()
		scheduleTransform()
	}, [clearWheelTransition, scheduleTransform])

	const setImageElement = useCallback(
		(node: HTMLImageElement | null) => {
			imageElementRef.current = node
			if (node) applyTransform()
		},
		[applyTransform]
	)

	const handleWheel = useCallback(
		(event: WheelEvent<HTMLDivElement>) => {
			if (!enabled) return

			event.preventDefault()
			event.stopPropagation()

			const currentTransform = transformRef.current
			const nextScale = getLimitedScale(currentTransform.scale - event.deltaY * DESKTOP_WHEEL_ZOOM_STEP)

			if (nextScale === currentTransform.scale) return

			const imageElement = imageElementRef.current
			if (imageElement) imageElement.style.transition = DESKTOP_WHEEL_ZOOM_TRANSITION

			transformRef.current = getTransformFromWheel({
				currentTransform,
				imageElement,
				nextScale,
				originX: event.clientX,
				originY: event.clientY,
				viewportElement: event.currentTarget
			})
			scheduleTransform()
			scheduleWheelTransitionReset()
		},
		[enabled, scheduleTransform, scheduleWheelTransitionReset]
	)

	const handlePointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (!enabled || transformRef.current.scale <= 1 || event.button !== 0) return

			event.preventDefault()
			event.stopPropagation()
			event.currentTarget.setPointerCapture(event.pointerId)
			clearWheelTransition()

			const { width, height } = event.currentTarget.getBoundingClientRect()
			dragPointRef.current = { x: event.clientX, y: event.clientY }
			dragPointerIdRef.current = event.pointerId
			dragViewportSizeRef.current = { width, height }
		},
		[clearWheelTransition, enabled]
	)

	const handlePointerMove = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			const dragPoint = dragPointRef.current
			const viewportSize = dragViewportSizeRef.current
			if (!enabled || !dragPoint || !viewportSize || dragPointerIdRef.current !== event.pointerId) return

			event.preventDefault()
			event.stopPropagation()

			const currentTransform = transformRef.current
			const deltaX = event.clientX - dragPoint.x
			const deltaY = event.clientY - dragPoint.y

			dragPointRef.current = { x: event.clientX, y: event.clientY }
			transformRef.current = getLimitedTransform(
				{
					scale: currentTransform.scale,
					x: currentTransform.x + deltaX,
					y: currentTransform.y + deltaY
				},
				viewportSize
			)
			applyTransform()
		},
		[applyTransform, enabled]
	)

	const handlePointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
		if (dragPointerIdRef.current === event.pointerId && event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId)
		}

		dragPointRef.current = null
		dragPointerIdRef.current = null
		dragViewportSizeRef.current = null
	}, [])

	useEffect(() => {
		if (!enabled) resetWheelZoom()
	}, [enabled, resetWheelZoom])

	useEffect(() => {
		return () => {
			if (transformFrameRef.current !== null) cancelAnimationFrame(transformFrameRef.current)
			if (transitionTimeoutRef.current !== null) window.clearTimeout(transitionTimeoutRef.current)
		}
	}, [])

	return {
		desktopZoomImageRef: setImageElement,
		handleDesktopZoomPointerDown: handlePointerDown,
		handleDesktopZoomPointerMove: handlePointerMove,
		handleDesktopZoomPointerUp: handlePointerUp,
		handleDesktopZoomWheel: handleWheel,
		resetDesktopWheelZoom: resetWheelZoom
	}
}
